import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '4rem'
});

// 英雄区块
const HeroSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: '4rem 1rem',
  background: 'linear-gradient(135deg, $backgroundPrimary 0%, $backgroundSecondary 100%)',
  borderRadius: '16px',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto 2rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 主要内容区块
const ContentSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem'
});

// 内容行，可以水平或垂直显示
const ContentRow = styled('div', {
  display: 'flex',
  gap: '2rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  },
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
        '@media (max-width: 768px)': {
          flexDirection: 'column'
        }
      }
    }
  }
});

// 内容列
const ContentColumn = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1.5rem'
});

// 内容标题
const ContentTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  '@media (max-width: 768px)': {
    fontSize: '1.5rem'
  }
});

// 内容描述
const ContentDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 特性列表
const FeatureList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '1rem 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 特性项
const FeatureItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '1rem'
});

// 特性图标
const FeatureIcon = styled('div', {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '$primary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

// 特性文本
const FeatureText = styled('p', {
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: 0
});

// 图片容器
const ImageContainer = styled('div', {
  flex: 1,
  height: '300px',
  borderRadius: '12px',
  overflow: 'hidden',
  background: '$backgroundSecondary'
});

// 图片样式
const Image = styled('img', {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});

// 操作按钮容器
const ActionButtons = styled('div', {
  display: 'flex',
  gap: '1rem',
  marginTop: '2rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  }
});

// 主要按钮
const PrimaryButton = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: '$primary',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryDark'
  }
});

// 次要按钮
const SecondaryButton = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: 'transparent',
  color: '$primary',
  border: '1px solid $primary',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryLight',
    color: '$primary'
  }
});

// 矿区卡片容器
const LocationCardsContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
  gap: '1.5rem',
  marginTop: '2rem'
});

// 矿区卡片
const LocationCard = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 5px 15px rgba(0, 0, 0, 0.1)',
  backgroundColor: '$backgroundSecondary',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)'
  }
});

// 矿区图片
const LocationImage = styled('img', {
  width: '100%',
  height: '150px',
  objectFit: 'cover'
});

// 矿区信息
const LocationInfo = styled('div', {
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem'
});

// 矿区名称
const LocationName = styled('h3', {
  fontSize: '1.25rem',
  fontWeight: 'bold',
  margin: 0
});

// 矿区特性
const LocationFeature = styled('p', {
  fontSize: '0.875rem',
  color: '$textSecondary',
  margin: 0
});

/**
 * 矿机托管概览页面组件
 * 介绍矿机托管服务的主要特点和优势
 */
const MiningHotelOverview: React.FC = () => {
  const { t } = useTranslation();
  
  // 矿区数据
  const locations = [
    {
      id: 'russia',
      name: t('products.miningHotel.locations.russia.name'),
      image: '/images/products/location-russia.jpg',
      power: t('products.miningHotel.locations.russia.power'),
      energy: t('products.miningHotel.locations.russia.energy'),
      cost: t('products.miningHotel.locations.russia.cost')
    },
    {
      id: 'texas',
      name: t('products.miningHotel.locations.texas.name'),
      image: '/images/products/location-texas.jpg',
      power: t('products.miningHotel.locations.texas.power'),
      energy: t('products.miningHotel.locations.texas.energy'),
      cost: t('products.miningHotel.locations.texas.cost')
    },
    {
      id: 'iceland',
      name: t('products.miningHotel.locations.iceland.name'),
      image: '/images/products/location-iceland.jpg',
      power: t('products.miningHotel.locations.iceland.power'),
      energy: t('products.miningHotel.locations.iceland.energy'),
      cost: t('products.miningHotel.locations.iceland.cost')
    },
    {
      id: 'kazakhstan',
      name: t('products.miningHotel.locations.kazakhstan.name'),
      image: '/images/products/location-kazakhstan.jpg',
      power: t('products.miningHotel.locations.kazakhstan.power'),
      energy: t('products.miningHotel.locations.kazakhstan.energy'),
      cost: t('products.miningHotel.locations.kazakhstan.cost')
    }
  ];
  
  return (
    <PageContainer>
      <HeroSection>
        <Title>{t('products.miningHotel.hero.title')}</Title>
        <Subtitle>{t('products.miningHotel.hero.subtitle')}</Subtitle>
        <ActionButtons>
          <PrimaryButton to="/products/mining-hotel/services">
            {t('products.miningHotel.hero.viewServices')}
          </PrimaryButton>
          <SecondaryButton to="/products/mining-hotel/application">
            {t('products.miningHotel.hero.applyHosting')}
          </SecondaryButton>
        </ActionButtons>
      </HeroSection>
      
      <ContentSection>
        {/* 服务介绍 */}
        <ContentRow>
          <ContentColumn>
            <ContentTitle>{t('products.miningHotel.intro.title')}</ContentTitle>
            <ContentDescription>{t('products.miningHotel.intro.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.intro.feature1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.intro.feature2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.intro.feature3')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.intro.feature4')}</FeatureText>
              </FeatureItem>
            </FeatureList>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/mining-hotel-intro.jpg" alt="Mining Hotel" />
          </ImageContainer>
        </ContentRow>
        
        {/* 矿机分拆介绍 */}
        <ContentRow reverse>
          <ContentColumn>
            <ContentTitle>{t('products.miningHotel.sharing.title')}</ContentTitle>
            <ContentDescription>{t('products.miningHotel.sharing.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.sharing.feature1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.sharing.feature2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.miningHotel.sharing.feature3')}</FeatureText>
              </FeatureItem>
            </FeatureList>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/mining-sharing.jpg" alt="矿机分拆" />
          </ImageContainer>
        </ContentRow>
        
        {/* 我们的矿场 */}
        <div>
          <ContentTitle>{t('products.miningHotel.locations.title')}</ContentTitle>
          <ContentDescription>{t('products.miningHotel.locations.description')}</ContentDescription>
          
          <LocationCardsContainer>
            {locations.map(location => (
              <LocationCard key={location.id}>
                <LocationImage src={location.image} alt={location.name} />
                <LocationInfo>
                  <LocationName>{location.name}</LocationName>
                  <LocationFeature>{t('products.miningHotel.locations.power')}: {location.power}</LocationFeature>
                  <LocationFeature>{t('products.miningHotel.locations.energy')}: {location.energy}</LocationFeature>
                  <LocationFeature>{t('products.miningHotel.locations.cost')}: {location.cost}</LocationFeature>
                </LocationInfo>
              </LocationCard>
            ))}
          </LocationCardsContainer>
          
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <PrimaryButton to="/products/mining-hotel/facilities">
              {t('products.miningHotel.locations.viewFacilities')}
            </PrimaryButton>
          </div>
        </div>
        
        {/* 成为托管客户 */}
        <ContentRow>
          <ContentColumn>
            <ContentTitle>{t('products.miningHotel.callToAction.title')}</ContentTitle>
            <ContentDescription>{t('products.miningHotel.callToAction.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>1</FeatureIcon>
                <FeatureText>{t('products.miningHotel.callToAction.step1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>2</FeatureIcon>
                <FeatureText>{t('products.miningHotel.callToAction.step2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>3</FeatureIcon>
                <FeatureText>{t('products.miningHotel.callToAction.step3')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>4</FeatureIcon>
                <FeatureText>{t('products.miningHotel.callToAction.step4')}</FeatureText>
              </FeatureItem>
            </FeatureList>
            <PrimaryButton to="/products/mining-hotel/application">
              {t('products.miningHotel.callToAction.applyNow')}
            </PrimaryButton>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/mining-hotel-apply.jpg" alt="申请托管" />
          </ImageContainer>
        </ContentRow>
      </ContentSection>
    </PageContainer>
  );
};

export default MiningHotelOverview; 